import { util, dia, ui, layout, V, elementTools, linkTools, highlighters } from '@joint/plus';
import { Header, Task as TaskShape, Dependency as DependencyShape, AddButton } from '../shapes';
var HEADER_HEIGHT = 40;
var MIN_BOARD_HEIGHT = 800;
var Kanban = /** @class */ (function () {
    function Kanban(options) {
        var _this = this;
        this.topLeft = options.topLeft;
        this.columns = options.columns;
        this.paper = options.paper;
        this.taskMargin = options.taskMargin || 10;
        this.taskWidth = options.taskWidth || 300;
        this.taskHeight = options.taskHeight || 200;
        this.columnMargin = options.columnMargin || 30;
        this.showDependencyTool = options.showDependencyTool || false;
        this.createHeader();
        this.createTasks(options.tasks);
        this.createDependencies(options.dependencies);
        this.createAddButtons();
        var layoutOptions = {
            direction: layout.StackLayout.Directions.TopBottom,
            topLeft: {
                x: this.topLeft.x,
                y: this.topLeft.y + this.taskMargin + HEADER_HEIGHT,
            },
            stackCount: this.columns.length,
            stackSize: this.taskWidth,
            stackGap: this.columnMargin,
            stackElementGap: this.taskMargin,
            setAttributes: function (el, _a) {
                var position = _a.position;
                el.set('position', position, { ignoreCommandManager: true });
            }
        };
        var layoutView = this.layoutView = new ui.StackLayoutView({
            layoutOptions: layoutOptions,
            paper: this.paper,
            modifyInsertElementIndex: function (elementIndexOptions) {
                // The last element in the stack is always the add-button
                var insertElementIndex = elementIndexOptions.insertElementIndex, targetStack = elementIndexOptions.targetStack;
                return Math.min(insertElementIndex, targetStack.elements.length - 1);
            },
            canInteract: function (elementView) {
                var element = elementView.model;
                return TaskShape.isTask(element);
            }
        });
        this.resizeBoard();
        layoutView.model.on('update', function () { return _this.resizeBoard(); });
        this.attachPaperEvents();
    }
    Kanban.prototype.attachPaperEvents = function () {
        var _this = this;
        var paper = this.paper;
        paper.on('element:pointerup', function (elementView) {
            if (!TaskShape.isTask(elementView.model))
                return;
            _this.selectTask(elementView);
        });
        paper.on('blank:pointerdown', function () {
            _this.unselectTask();
        });
        paper.on('link:mouseenter', function (linkView) {
            var toolsView = new dia.ToolsView({
                tools: [
                    new linkTools.Remove({
                        distance: 10,
                        action: function () {
                            linkView.model.remove();
                        }
                    })
                ]
            });
            linkView.addTools(toolsView);
        });
        paper.on('link:mouseleave', function (linkView) {
            linkView.removeTools();
        });
        paper.on('element:pointerclick', function (elementView) {
            var element = elementView.model;
            if (!AddButton.isAddButton(element))
                return;
            _this.addTask(element.get('stackIndex'));
        });
        paper.on('element:pointerdblclick', function (taskView, evt) {
            if (!TaskShape.isTask(taskView.model))
                return;
            var targetSelector = taskView.findAttribute('joint-selector', evt.target);
            var editHeader = ('header' === targetSelector) || ('headerText' === targetSelector);
            _this.editTask(taskView, editHeader);
        });
    };
    Kanban.prototype.createHeader = function () {
        var _this = this;
        var graph = this.paper.model;
        this.columns.forEach(function (column, index) {
            var position = {
                x: _this.topLeft.x + index * (_this.taskWidth + _this.columnMargin),
                y: _this.topLeft.y
            };
            var size = {
                width: _this.taskWidth,
                height: HEADER_HEIGHT,
            };
            graph.addCell(_this.createHeaderElement(column, position, size));
        });
    };
    Kanban.prototype.createHeaderElement = function (column, position, size) {
        return new Header({
            id: 'column-header-' + column.state,
            position: position,
            size: size,
            attrs: {
                label: {
                    text: column.name
                },
                marker: {
                    fill: column.color
                }
            }
        });
    };
    Kanban.prototype.createTasks = function (tasks) {
        var _this = this;
        var graph = this.paper.model;
        tasks.forEach(function (task, index) {
            var size = {
                height: _this.taskHeight,
                width: _this.taskWidth
            };
            graph.addCell(_this.createTaskElement(task, size, index));
        });
    };
    Kanban.prototype.createTitle = function (name, description) {
        return "".concat(name, "\n\n").concat(description);
    };
    Kanban.prototype.createTaskElement = function (task, size, index) {
        var id = task.id, state = task.state, name = task.name, description = task.description;
        return new TaskShape({
            id: id,
            size: size,
            attrs: {
                root: {
                    title: this.createTitle(name, description)
                },
                headerText: {
                    text: name
                },
                bodyText: {
                    text: description
                }
            },
            stackIndex: this.getStackIndexFromState(state),
            stackElementIndex: index
        });
    };
    Kanban.prototype.createAddButtons = function () {
        var _this = this;
        var graph = this.paper.model;
        this.columns.forEach(function (column, index) {
            var addButton = new AddButton({
                id: 'add-button-' + index,
                size: {
                    width: _this.taskWidth,
                    height: 40,
                },
                attrs: {
                    label: {
                        fill: column.color
                    }
                },
                stackIndex: index
            });
            graph.addCell(addButton);
        });
    };
    Kanban.prototype.createDependencies = function (dependencies) {
        var graph = this.paper.model;
        dependencies.forEach(function (dependency) {
            var dependencyShape = new DependencyShape({
                id: dependency.id,
                source: { id: dependency.source },
                target: { id: dependency.target },
            });
            graph.addCell(dependencyShape);
        });
    };
    Kanban.prototype.getStackIndexFromState = function (state) {
        return this.columns.findIndex(function (column) { return column.state === state; });
    };
    Kanban.prototype.resizeBoard = function () {
        var _a = this, paper = _a.paper, layoutView = _a.layoutView;
        var size = paper.getComputedSize();
        paper.setDimensions(size.width, Math.max(layoutView.model.bbox.height + this.topLeft.y + HEADER_HEIGHT + 2 * this.taskMargin, MIN_BOARD_HEIGHT));
    };
    // Public Methods
    Kanban.prototype.editTask = function (taskView, editHeader) {
        var _this = this;
        if (editHeader === void 0) { editHeader = false; }
        var paper = this.paper;
        var task = taskView.model;
        this.unselectTask();
        var bbox = task.getBBox();
        this.textarea = document.createElement('textarea');
        var textarea = this.textarea;
        // Position, Size & Styling
        textarea.style.position = 'absolute';
        textarea.style.boxSizing = 'border-box';
        textarea.style.transformOrigin = '0 0';
        textarea.style.textAlign = 'start';
        textarea.style.alignContent = 'center';
        textarea.style.textAlign = 'left';
        textarea.style.letterSpacing = '0';
        textarea.style.resize = 'none';
        textarea.style.lineHeight = '1.5em';
        // Content
        var textPath;
        if (editHeader) {
            textPath = 'headerText/text';
            textarea.style.padding = "".concat(15, "px ").concat(20, "px");
            textarea.style.transform = V.matrixToTransformString(paper.matrix().translate(bbox.x, bbox.y));
            textarea.style.width = "".concat(bbox.width, "px");
            textarea.style.height = "".concat(50, "px");
            textarea.style.fontSize = task.attr('headerText/fontSize') + 'px';
            textarea.style.fontFamily = task.attr('headerText/fontFamily');
            textarea.style.fontWeight = task.attr('headerText/fontWeight');
            textarea.style.color = task.attr('headerText/fill');
            textarea.style.overflowY = 'hidden';
            textarea.wrap = 'off';
        }
        else {
            textPath = 'bodyText/text';
            var headerHeight = 40;
            textarea.style.padding = "".concat(10, "px ").concat(20, "px");
            textarea.style.transform = V.matrixToTransformString(paper.matrix().translate(bbox.x, bbox.y + headerHeight));
            textarea.style.width = "".concat(bbox.width, "px");
            textarea.style.height = "".concat(bbox.height - headerHeight, "px");
            textarea.style.fontSize = task.attr('bodyText/fontSize') + 'px';
            textarea.style.fontFamily = task.attr('bodyText/fontFamily');
            textarea.style.fontWeight = task.attr('bodyText/fontWeight');
            textarea.style.color = task.attr('bodyText/fill');
        }
        textarea.value = task.attr(textPath);
        // Append the this.textarea to the paper
        paper.el.appendChild(textarea);
        textarea.focus();
        // Select all text
        textarea.setSelectionRange(0, textarea.value.length);
        // Attach Event Listeners
        textarea.addEventListener('blur', function () {
            task.startBatch('text');
            task.attr(textPath, textarea.value);
            task.attr('root/title', _this.createTitle(task.attr('headerText/text'), task.attr('bodyText/text')));
            task.stopBatch('text');
            textarea.remove();
        });
        textarea.addEventListener('keyup', function (evt) {
            if (evt.key === 'Enter' && (!evt.shiftKey || textarea.wrap === 'off')) {
                var index = textarea.selectionEnd;
                textarea.value = textarea.value.slice(0, index - 1) + textarea.value.slice(index);
                textarea.blur();
                _this.selectTask(taskView);
            }
            if (evt.key === 'Escape') {
                textarea.value = task.attr(textPath);
                textarea.blur();
                _this.selectTask(taskView);
            }
        });
    };
    Kanban.prototype.addTask = function (stackIndex) {
        var stack = this.layoutView.model.stacks[stackIndex];
        var task = {
            state: this.columns[stackIndex].state,
            name: 'New Task',
            description: 'Description'
        };
        var size = {
            height: this.taskHeight,
            width: this.taskWidth
        };
        var taskElement = this.createTaskElement(task, size);
        this.layoutView.model.insertElement(taskElement, stackIndex, stack.elements.length - 1);
    };
    Kanban.prototype.selectTask = function (taskView) {
        var _this = this;
        this.unselectTask();
        this.selectedTask = taskView.model;
        var tools = [
            new elementTools.Remove({
                useModelGeometry: true,
                x: '100%',
                y: 15,
                offset: { x: 10 },
                action: function () {
                    taskView.model.remove();
                    _this.layoutView.model.update();
                }
            })
        ];
        if (this.showDependencyTool) {
            tools.push(new elementTools.Connect({
                useModelGeometry: true,
                x: '100%',
                y: 35,
                offset: { x: 10 }
            }));
        }
        var toolsView = new dia.ToolsView({ tools: tools });
        taskView.addTools(toolsView);
        this.highlighter = highlighters.addClass.add(taskView, 'root', 'selection', {
            className: 'highlighter-selection'
        });
    };
    Kanban.prototype.unselectTask = function () {
        if (this.highlighter) {
            this.highlighter.remove();
            this.highlighter = null;
        }
        if (this.textarea) {
            this.textarea.blur();
        }
        this.paper.removeTools();
        delete this.selectedTask;
    };
    Object.defineProperty(Kanban.prototype, "tasks", {
        get: function () {
            var _this = this;
            var graph = this.paper.model;
            var tasks = [];
            var taskElements = util.sortBy(graph.getElements(), function (el) { return el.get('stackElementIndex'); });
            taskElements.forEach(function (el) {
                if (!TaskShape.isTask(el))
                    return;
                tasks.push({
                    id: el.id,
                    state: _this.columns[el.get('stackIndex')].state,
                    name: el.attr('headerText/text'),
                    description: el.attr('bodyText/text')
                });
            });
            return tasks;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(Kanban.prototype, "dependencies", {
        get: function () {
            var graph = this.paper.model;
            return graph.getLinks().map(function (dependency) {
                return {
                    id: dependency.id,
                    source: dependency.source().id,
                    target: dependency.target().id
                };
            });
        },
        enumerable: false,
        configurable: true
    });
    return Kanban;
}());
export { Kanban };
